import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { loadModelAction } from "store/actions/modal";
import { addToNewCollection } from "store/actions/collections";
import { loadCustomProperties } from 'store/actions/table'
import { useDashboardState } from "components/DashboardContext";
import classNames from 'classnames';
import { CLEAR_PARAMETERS_OF_SELECTED_ELEMENTS_FROM_STATE, RESET_TABLE, SET_CURRENT_TABLE_CELL } from 'store/constans';
import { onSelectionChange } from 'store/actions/filter';
import SetCustomParameterModal from 'components/modals/SetCustomParameterModal/SetCustomParameterModal';
import {
  getSelection,
  loadModel,
  registerRightClickMethods,
  registerSelectionChangeMethod,
  registerTimebar,
  resizeViewer,
  isolateSelection,
  hideSelection,
  showAll,
  toggleGhosting,
  registerCustomParametersMethods,
  invokeCustomParametersButtonOnClick,
  registerExportSelection,
  registerClearCustomPropertiesValuesFromRedux,
  setCustomParameterValues,
} from "utils/builder/IScheduleLogic";
import QuickStats from "components/QuickStats";
import UniversalButton from "components/uiComponents/UniversalButton";
import { ReactComponent as ChangeHeightIcon } from "static/icons/multiselection.svg";
import { ReactComponent as IsolateSelection } from "static/icons/isolate-selection.svg";
import { ReactComponent as HideSelection } from "static/icons/hide-selection.svg";
import { ReactComponent as ShowAll } from "static/icons/show-all.svg";
import { ReactComponent as ToggleGhosting } from "static/icons/toggle-ghosting.svg";
import SaveCollectionModal from "../modals/SaveCollectionModal";
import { addTranslationsToState } from "store/actions/translations";
import DraggableCustomParameters from "components/DraggableDiv/DraggableCustomParameters"
import { getParametersOfSelectedElements, saveSelectionOfViewer } from "../../store/actions/viewer";
import { FullScreenAwarePortal } from "components/FullScreenAwarePortal/FullScreenAwarePortal";
import { showToast } from 'utils/helpers';
import { ModalContext } from '../ModalContext';
import useSetting from 'components/sidebarComponents/SidebarProject/use-settings';
import { getSessionId } from 'utils/crossTabSync';
import TimelineBar from 'components/uiComponents/Timeline/TimelineBar';
import TimelinePanel from 'components/uiComponents/Timeline/TimelinePanel';
import useTimeline from 'hooks/useTimeline';
import './EditorSection.scss';

const EditorSection = ({ autodesk, projectData, quickCounterData, sidebarIsOpened, table, onTimelineToggle }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const viewerRef = useRef();

  const { isTableHidden, setIsTableHidden, setIsModalLoaded, isTranslateModelEnabled } = useDashboardState();
  const { compare, quickInfoCompare } = useSelector((state) => state.viewer);
  const currentTableState = useSelector((state) => state.table?.currentTableState);
  const { temporarySetting } = useSetting();
  const { metaData } = temporarySetting;
  const { handleModal } = useContext(ModalContext);

  const modelId = projectData?.idToOpen || null;

  const [toolbarState, setToolbarState] = useState({ isolateSelection: false, hideSelection: false, showAll: false, toggleGhosting: true });
  const [isCustomParamsPanelOpen, setCustomParametersDraggable] = useState(false);
  const [timelineQuickStats, setTimelineQuickStats] = useState(null);
  const [hasViewerSelection, setHasViewerSelection] = useState(false);

  const {
    pickerState: timelinePickerState,
    barState: timelineBarState,
    openPicker: openTimelinePicker,
    onParamPick: handleTimelineParamPick,
    onDateChange: handleTimelineDateChange,
    onCloseBar: handleCloseTimelineBar,
  } = useTimeline();

  const prevTableHiddenRef = useRef(null);

  useEffect(() => {
    if (timelineBarState.open) {
      if (!prevTableHiddenRef.current) {
        prevTableHiddenRef.current = isTableHidden;
      } 
      setIsTableHidden(false);
      onTimelineToggle?.(true);
    } else if (prevTableHiddenRef.current !== null) {
      setIsTableHidden(prevTableHiddenRef.current);
      onTimelineToggle?.(false);
      prevTableHiddenRef.current = null;
    }
  }, [timelineBarState.open, isTableHidden, setIsTableHidden, onTimelineToggle]);

  useEffect(() => {
    setTimeout(() => resizeViewer(), 510);
  }, [isTableHidden]);

  // Check viewer selection
  useEffect(() => {
    const checkViewerSelection = async () => {
      try {
        const selection = await getSelection();
        setHasViewerSelection(selection && selection.length > 0);
      } catch {
        setHasViewerSelection(false);
      }
    };

    // Check selection periodically and when table visibility changes
    const interval = setInterval(checkViewerSelection, 500);
    checkViewerSelection();

    return () => clearInterval(interval);
  }, [isTableHidden, table]);

  const hasTimelineStats = Array.isArray(timelineQuickStats) && timelineQuickStats.length > 0 && timelineBarState.open;
  const effectiveQuickStats = hasTimelineStats ? timelineQuickStats : quickCounterData;

  const hasTableSelection = useSelector((state) => {
    if (!modelId) return false;
    const { getCurrentNamespace } = require('utils/crossTabSync');
    const currentNs = getCurrentNamespace();
    if (!currentNs || currentNs !== modelId) return false;

    const tab = state.table.tabs.find((tab) =>
      tab.id === 1 && tab.modelId === modelId
    );
    return tab?.selectedRows && tab.selectedRows.length > 0;
  });

  const hasTableSelectItems = useSelector((state) => {
    if (!modelId) return false;
    const { getCurrentNamespace } = require('utils/crossTabSync');
    const currentNs = getCurrentNamespace();
    if (!currentNs || currentNs !== modelId) return false;

    return state.table.tabs.some(tab => {
      if (tab.modelId && tab.modelId !== modelId) return false;
      if (!tab.modelId && currentNs !== modelId) return false;
      return tab.selectItems && Object.keys(tab.selectItems).length > 0;
    });
  });

  // Show QuickStats only if:
  // 1. TimeBar is active (hasTimelineStats), OR
  // 2. There are selected elements in table (selectedRows or selectItems) OR in viewer
  const hasSelection = hasTimelineStats || hasTableSelection || hasTableSelectItems || hasViewerSelection;

  const shouldShowQuickStats =
    effectiveQuickStats &&
    effectiveQuickStats.length > 0 &&
    effectiveQuickStats[0]?.value &&
    ((isTableHidden && !compare.isModeCompare) || hasSelection);

  useEffect(() => {
    const handler = (e) => {
      const data = e?.detail;
      if (Array.isArray(data)) {
        setTimelineQuickStats(data);
      } else {
        setTimelineQuickStats(null);
      }
    };
    window.addEventListener('bs:timeline-quickcounter', handler);
    return () => window.removeEventListener('bs:timeline-quickcounter', handler);
  }, []);

  useEffect(() => {
    if (!timelineBarState.open) {
      setTimelineQuickStats(null);
    }
  }, [timelineBarState.open]);

  const toggleTable = () => setIsTableHidden(!isTableHidden);
  const closeModal = () => handleModal(null, true);

  const openSetCustomParameters = () => handleModal(<SetCustomParameterModal closeModal={closeModal} />);

  const saveAsCollection = async () => {
    const selection = await getSelection();
    handleModal(
      <SaveCollectionModal
        tableInfo={{
          collection: { properties: ['Type Name', 'Description', 'Length', 'Area', 'Volume'], groupBy: [] },
          lineElements: selection,
        }}
        closeCollectionModal={closeModal}
      />
    );
  };

  const createNewCollection = async () => {
    const selection = await getSelection();
    try {
      await dispatch(
        addToNewCollection({
          elements: selection,
          properties: ['Type Name', 'Description', 'Length', 'Area', 'Volume'],
          groups: [],
        })
      );
      showToast(true, t('selection_created'));
    } catch {
      showToast(false, t('selection_not_created'));
    }
  };

  const onSelectionChangeQuickStats = (result) => dispatch(onSelectionChange(result));

  const initViewer = useCallback(() => {
    if (!projectData || !viewerRef.current || !autodesk || viewerRef.current.children.length >= 1) return;

    dispatch({ type: RESET_TABLE });

    loadModel(
      projectData,
      (modelResult) => {
        setIsModalLoaded({ status: true });
        dispatch(loadModelAction(modelResult));
        dispatch(loadCustomProperties());
        dispatch(addTranslationsToState(modelResult.translations));
        try {
          const sid = getSessionId();
          localStorage.setItem('bs:main-session', sid);
        } catch { }
        registerRightClickMethods(saveAsCollection, createNewCollection, null, openSetCustomParameters);
        toggleGhosting(true);
      },
      viewerRef.current,
      isTranslateModelEnabled
    );

    registerSelectionChangeMethod(onSelectionChangeQuickStats);
    registerCustomParametersMethods({ setCustomParametersDraggable, dispatch, getParametersOfSelectedElements });

    registerTimebar(openTimelinePicker, handleCloseTimelineBar);

    registerExportSelection((selection) => dispatch(saveSelectionOfViewer(selection)));
    registerClearCustomPropertiesValuesFromRedux(() => dispatch({ type: CLEAR_PARAMETERS_OF_SELECTED_ELEMENTS_FROM_STATE }));
  }, [
    projectData,
    autodesk,
    isTranslateModelEnabled,
    dispatch,
    setIsModalLoaded,
    openTimelinePicker,
    handleCloseTimelineBar,
  ]);

  useEffect(() => {
    initViewer();
  }, [initViewer]);

  useEffect(() => {
    const sessionId = getSessionId();
    const isMain = () => {
      try {
        return localStorage.getItem('bs:main-session') === sessionId;
      } catch {
        return false;
      }
    };

    const onStorage = async (e) => {
      if (e.storageArea !== localStorage || !e.newValue || e.key !== 'bs:tab-actions' || !isMain()) return;
      try {
        const message = JSON.parse(e.newValue);
        if (message?.from === sessionId) return;
        const action = message?.action;
        if (!action) return;

        if (action.type === SET_CURRENT_TABLE_CELL) {
          const { columnId, lineElements, newValue } = action.payload || {};
          if (columnId && Array.isArray(lineElements)) {
            try {
              await setCustomParameterValues(columnId, lineElements, newValue);
            } catch { }
          }
        }
      } catch { }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isSomeCollectionSelected = () => Object.values(compare.collections).flat(2).some((c) => c.checked);

  const onToolbarToggle = (key, action) => {
    setToolbarState((s) => ({ ...s, [key]: !s[key] }));
    action();
  };

  return (
    <div className="editor-section">
      <div className="editor-section__body">
        {!!shouldShowQuickStats && (
          <QuickStats
            isAdvanceFilterOpen={metaData?.isSidebarOpen}
            quickCounterData={effectiveQuickStats}
            sidebarIsOpened={sidebarIsOpened}
          />
        )}

        {isSomeCollectionSelected() && <QuickStats quickCounterData={quickInfoCompare} sidebarIsOpened={sidebarIsOpened} isCompare />}

        <FullScreenAwarePortal visible={timelinePickerState.open} htmlElementId={"timeline-panel"}>
          <TimelinePanel title={t('Timeline') || 'Timeline'} params={timelinePickerState.params} onPick={handleTimelineParamPick} />
        </FullScreenAwarePortal>

        <FullScreenAwarePortal visible={timelineBarState.open} htmlElementId={"timeline-bar"}>
          <TimelineBar min={timelineBarState.min} max={timelineBarState.max} value={timelineBarState.value} locked={timelineBarState.locked} onChange={handleTimelineDateChange} onClose={handleCloseTimelineBar} sidebarIsOpened={sidebarIsOpened} />
        </FullScreenAwarePortal>

        <div className="editor-panel" style={!!table.tabs.length && !compare.isModeCompare ? { bottom: '66px' } : { bottom: '16px' }}>
          <UniversalButton centerIcon={<ShowAll />} size="lg" labelColor="white" labelHoverColor="white" onClick={() => onToolbarToggle('showAll', showAll)} />
          <UniversalButton centerIcon={<IsolateSelection />} size="lg" labelColor="white" labelHoverColor="white" onClick={() => onToolbarToggle('isolateSelection', isolateSelection)} />
          <UniversalButton centerIcon={<HideSelection />} size="lg" labelColor="white" labelHoverColor="white" onClick={() => onToolbarToggle('hideSelection', hideSelection)} />
          <UniversalButton
            centerIcon={<ToggleGhosting />}
            size="lg"
            labelColor="white"
            labelHoverColor="white"
            backgroundColor={toolbarState.toggleGhosting ? 'orange' : 'gray6'}
            backgroundHoverColor={toolbarState.toggleGhosting ? 'orange-light' : 'gray5'}
            onClick={() => onToolbarToggle('toggleGhosting', () => toggleGhosting(!toolbarState.toggleGhosting))}
          />
        </div>

        {!!table.tabs.length && !compare.isModeCompare && (
          <div className="editor-section__height-button">
            <UniversalButton
              centerIcon={<ChangeHeightIcon />}
              size="lg"
              labelColor="white"
              labelHoverColor="white"
              backgroundColor={isTableHidden ? 'orange' : 'gray6'}
              backgroundHoverColor={isTableHidden ? 'orange-light' : 'gray5'}
              onClick={toggleTable}
            />
          </div>
        )}

        <div className={classNames({ 'editor-section__viewer': true, 'editor-section__viewer--short': !isTableHidden })} ref={viewerRef} />
      </div>
      <FullScreenAwarePortal
        visible={isCustomParamsPanelOpen}
        htmlElementId={"draggables"}
      >
        <DraggableCustomParameters onClose={invokeCustomParametersButtonOnClick} />
      </FullScreenAwarePortal>
    </div>
  );
};

export default EditorSection;